import React from 'react'
import ReactDOM from 'react-dom'
import {
    Container,
    Table,
    Row,
    Col,
    Label,
    Input,
    InputGroup,
    InputGroupText,
    Button,
} from 'reactstrap'
import SimpleDropdown from '../view/DropDownView'
import DrawingImageUploadView, { extensionForMimeType } from '../view/DrawingImageUploadView'
import { Quote } from '../model/Quote'
import { DrawingData } from '../model/DrawingData'
const {
    getQuote,
} = require('../model/request/quotes')
const {
    getDrawingMetadata,
} = require('../model/request/drawingMetadata')
const {
    getDrawingData,
    updateDrawingData,
    updateDrawingDataBatch,
} = require('../model/request/drawingData')
const {
    getFiles,
    getDownloadFileLink,
    replaceDrawingImage,
} = require('../model/request/files')
const {
    getCompanyLogoLink,
    uploadCompanyLogo,
} = require('../model/request/companyLogo')
const {
    generateDrawingDataPdf,
} = require('../model/request/pdf')

const PAINT_OPTIONS = ['Surface', 'Subsurface', 'N/A', 'Other']

// Human-readable labels for every field that can trigger the bulk-fill
// bar - matches the on-screen label/header text used elsewhere on this
// page exactly, rather than title-casing the raw field key (which reads
// as e.g. "Dimension_scale" for dimension_scale).
const FIELD_LABELS = {
    font: 'Font',
    mounting: 'Mounting',
    dimension_scale: 'Dimension Drawing Scale',
    overview_scale: 'Overview Image Scale',
    part_number: 'Part #',
    description: 'Description',
    material: 'Material',
    size: 'Size',
    thickness: 'Thickness',
    paint: 'Paint (Surface/Subsurface)',
    background_color: 'Background Color',
    textile_colors: 'Textile Colors',
    corners: 'Corners',
    edges: 'Edges',
    finish: 'Finish',
}

class DrawingDataViewController extends React.Component {

    constructor(props) {
        super(props)
        this.state = {
            customerName: null,
            signs: [], // [Sign]
            drawingDataBySignId: {}, // signId -> DrawingData
            metadataAttributesBySignId: {}, // signId -> attributes, for Add Part later
            dirtyBySignId: {}, // signId -> boolean
            bulkFill: null, // {signId, partIndex, field, value} | null
            files: [], // [{id, quote_id, name, sign_given_id, category}]
            navbarHeight: 0, // measured so the bulk-fill bar sits right below it
        }
    }

    componentDidMount() {
        this.refresh()
        this.measureNavbarHeight()
        window.addEventListener('resize', this.measureNavbarHeight)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.measureNavbarHeight)
        clearTimeout(this.bulkFillHideTimeout)
    }

    measureNavbarHeight = () => {
        const navbar = document.querySelector('.navbar')
        this.setState({ navbarHeight: navbar?.offsetHeight ?? 0 })
    }

    // Hides the bulk-fill bar once the user stops editing (blurs the
    // field) - delayed so a click on one of the bar's own buttons (which
    // blurs the field first) still has time to register before the bar
    // disappears. Cancelled by onFocus if the user tabs to another field
    // within the same sign instead of actually stepping away.
    scheduleHideBulkFillBar = () => {
        clearTimeout(this.bulkFillHideTimeout)
        this.bulkFillHideTimeout = setTimeout(
            () => this.setState({ bulkFill: null }), 200)
    }

    cancelHideBulkFillBar = () => {
        clearTimeout(this.bulkFillHideTimeout)
    }

    // Metadata rows are keyed by sign_given_id/part_id (see
    // DrawingMetadataViewController.js) - a sign can have a whole-sign row
    // (no part_id) plus one row per separately-specified part (part_id
    // "a", "b", ...). Returns every attributes object for this sign, in
    // the same order as DrawingMetadataViewController.js's own sort (the
    // whole-sign row first, then part rows alphabetically by part_id) -
    // that order also becomes the initial part_data row order.
    metadataAttributesListFor(metadataRows, sign) {
        const rows = metadataRows
            .filter((r) => r.sign_given_id === sign.given_id)
            .sort((a, b) => {
                if (!a.part_id) return -1
                if (!b.part_id) return 1
                return a.part_id.localeCompare(b.part_id)
            })
        return rows.length > 0 ? rows.map((r) => r.attributes) : [{}]
    }

    refresh() {
        this.props.turnOnLoader()

        Promise.all([
            getQuote(this.props.id),
            getDrawingData(this.props.id),
            getFiles(this.props.id),
        ]).then(([quoteResult, drawingDataResult, filesResult]) => {
            const quote = Quote.from(quoteResult.data.quote)
            const signs = quote.signs.filter((s) => s.quantity > 0)
            const savedRows = drawingDataResult.data.drawing_data
            const files = filesResult.data.files
            const savedBySignId = {}
            savedRows.forEach((row) => { savedBySignId[row.sign_id] = row })

            const missingSigns = signs.filter((s) => !savedBySignId[s.id])

            const buildState = (metadataRows) => {
                const drawingDataBySignId = {}
                const metadataAttributesBySignId = {}
                signs.forEach((sign) => {
                    const attributesList = this.metadataAttributesListFor(metadataRows, sign)
                    metadataAttributesBySignId[sign.id] = attributesList
                    drawingDataBySignId[sign.id] = savedBySignId[sign.id]
                        ? DrawingData.from(savedBySignId[sign.id])
                        : DrawingData.autoFillFor(sign, attributesList)
                })
                // Any sign that had no saved row yet just got fresh,
                // never-saved auto-filled data - that's already a pending
                // change, so its Save button starts enabled.
                const dirtyBySignId = {}
                signs.forEach((sign) => {
                    dirtyBySignId[sign.id] = !savedBySignId[sign.id]
                })

                this.setState({
                    customerName: quote.customer_name,
                    signs, drawingDataBySignId, metadataAttributesBySignId, dirtyBySignId, files,
                })
                this.props.turnOffLoader()
            }

            if (missingSigns.length === 0) {
                buildState([])
                return
            }

            getDrawingMetadata(this.props.id).then(
                (metadataResult) => buildState(metadataResult.data.metadata),
                (error) => {
                    this.props.showError(error)
                    buildState([])
                },
            )
        }, (error) => {
            this.props.showError(error)
            this.props.turnOffLoader()
        })
    }

    // Re-fetches just the file list (not drawing-data/dirty state) - used
    // as the drawing-image upload's onUploaded callback so uploading an
    // image doesn't disturb in-progress edits elsewhere on the page.
    refreshFiles() {
        return getFiles(this.props.id).then((result) => {
            this.setState({ files: result.data.files })
        }, (error) => {
            this.props.showError(error)
        })
    }

    findDrawingImage(signGivenId, category) {
        return this.state.files.find((f) =>
            f.sign_given_id === signGivenId && f.category === category) ?? null
    }

    signQuantity(sign) {
        return sign.quantity
    }

    markDirty(signId) {
        this.setState((state) => ({
            dirtyBySignId: { ...state.dirtyBySignId, [signId]: true },
        }))
    }

    updateSignField(signId, field, value) {
        this.setState((state) => {
            const drawingData = state.drawingDataBySignId[signId]
            drawingData[field] = value
            return {
                drawingDataBySignId: { ...state.drawingDataBySignId, [signId]: drawingData },
            }
        })
        this.markDirty(signId)
        this.setState({ bulkFill: { signId, partIndex: null, field, value } })
    }

    updatePartField(signId, partIndex, field, value) {
        this.setState((state) => {
            const drawingData = state.drawingDataBySignId[signId]
            drawingData.part_data[partIndex][field] = value
            return {
                drawingDataBySignId: { ...state.drawingDataBySignId, [signId]: drawingData },
            }
        })
        this.markDirty(signId)
        this.setState({ bulkFill: { signId, partIndex, field, value } })
    }

    addPart(signId) {
        this.setState((state) => {
            const drawingData = state.drawingDataBySignId[signId]
            const sign = state.signs.find((s) => s.id === signId)
            const newIndex = drawingData.part_data.length
            const attributes = state.metadataAttributesBySignId[signId][newIndex] ?? {}
            const newPart = DrawingData.autoFillPart(newIndex, attributes, sign)
            drawingData.part_data.push(newPart)
            return {
                drawingDataBySignId: { ...state.drawingDataBySignId, [signId]: drawingData },
            }
        })
        this.markDirty(signId)
    }

    removePart(signId, partIndex) {
        this.setState((state) => {
            const drawingData = state.drawingDataBySignId[signId]
            drawingData.part_data.splice(partIndex, 1)
            return {
                drawingDataBySignId: { ...state.drawingDataBySignId, [signId]: drawingData },
            }
        })
        this.markDirty(signId)
    }

    // Bulk-fill: copies the last-edited field's value out to a wider scope.
    // partIndex is null for a sign-level field (Font/Mounting), in which
    // case only "every sign" makes sense - there's no "every part of this
    // sign" or "matching part across signs" for a field that isn't per-part.
    applyBulkFill(scope) {
        const { signId, partIndex, field, value } = this.state.bulkFill
        this.setState((state) => {
            const drawingDataBySignId = { ...state.drawingDataBySignId }
            const dirtyBySignId = { ...state.dirtyBySignId }

            const applyToPart = (targetSignId, targetPartIndex) => {
                const drawingData = drawingDataBySignId[targetSignId]
                if (drawingData.part_data[targetPartIndex]) {
                    drawingData.part_data[targetPartIndex][field] = value
                    dirtyBySignId[targetSignId] = true
                }
            }
            const applyToSignField = (targetSignId) => {
                drawingDataBySignId[targetSignId][field] = value
                dirtyBySignId[targetSignId] = true
            }

            if (partIndex === null) {
                // Sign-level field - only "every sign on this job" applies.
                state.signs.forEach((sign) => applyToSignField(sign.id))
            } else if (scope === 'part') {
                drawingDataBySignId[signId].part_data.forEach((_, i) => applyToPart(signId, i))
            } else if (scope === 'sign') {
                state.signs.forEach((sign) => applyToPart(sign.id, partIndex))
            } else if (scope === 'job') {
                state.signs.forEach((sign) => {
                    drawingDataBySignId[sign.id].part_data.forEach((_, i) =>
                        applyToPart(sign.id, i))
                })
            }

            return { drawingDataBySignId, dirtyBySignId, bulkFill: null }
        })
    }

    saveSign(signId) {
        const drawingData = this.state.drawingDataBySignId[signId]
        this.props.turnOnLoader()
        return updateDrawingData(
            signId, drawingData.font, drawingData.mounting, drawingData.part_data,
            drawingData.dimension_scale, drawingData.overview_scale,
        ).then(
            () => {
                this.setState((state) => ({
                    dirtyBySignId: { ...state.dirtyBySignId, [signId]: false },
                }))
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            },
        )
    }

    saveAll() {
        const dirtySignIds = Object.keys(this.state.dirtyBySignId)
            .filter((signId) => this.state.dirtyBySignId[signId])
        this.props.turnOnLoader()
        const updates = dirtySignIds.map((signId) => {
            const drawingData = this.state.drawingDataBySignId[signId]
            return {
                'sign_id': signId,
                'font': drawingData.font,
                'mounting': drawingData.mounting,
                'part_data': drawingData.part_data,
                'dimension_scale': drawingData.dimension_scale,
                'overview_scale': drawingData.overview_scale,
            }
        })
        updateDrawingDataBatch(updates).then(
            () => {
                this.setState((state) => {
                    const dirtyBySignId = { ...state.dirtyBySignId }
                    dirtySignIds.forEach((signId) => { dirtyBySignId[signId] = false })
                    return { dirtyBySignId }
                })
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            },
        )
    }

    // Single global bar (not one per field/row) - pinned just below the
    // navbar so it stays visible no matter how far down the page the
    // triggering field was. Reuses the .navbar class itself (App.css) for
    // the blur/background treatment plus Bootstrap's shadow-lg utility for
    // depth, rather than inventing one-off CSS - only position/z-index
    // (unique to this bar's placement) are set inline. Rendered via a
    // portal straight to document.body, not inline in this component's
    // own <Container> - several selectable themes (e.g. LiquidGlass.css)
    // put a backdrop-filter on .container, and any filter/backdrop-filter
    // on an ancestor creates a new containing block for position:fixed
    // descendants, which silently turns "fixed to the viewport" into
    // "fixed to that ancestor" (i.e. it scrolls away with the page). The
    // portal sidesteps that regardless of which theme is active.
    renderBulkFillBar() {
        const { bulkFill, navbarHeight } = this.state
        if (!bulkFill) {
            return null
        }
        const { partIndex, field, value } = bulkFill
        const fieldLabel = FIELD_LABELS[field] ?? field
        return ReactDOM.createPortal(
            <div className="navbar shadow-lg" style={{
                position: 'fixed', top: navbarHeight, left: 0, right: 0,
                zIndex: 998, padding: '16px 0',
            }}>
                <Container>
                    <Row className="align-items-center">
                        <Col>
                            <Label style={{ marginBottom: 0, marginRight: '16px' }}>
                                Apply "{value}" to {fieldLabel} on
                            </Label>
                            {partIndex === null ?
                                <Button size="sm" onClick={() => this.applyBulkFill('job')}>
                                    Every Sign on this Job
                                </Button>
                                : <>
                                    <Button size="sm" style={{ marginRight: '8px' }}
                                        onClick={() => this.applyBulkFill('part')}>
                                        Every Part of this Sign
                                    </Button>
                                    <Button size="sm" style={{ marginRight: '8px' }}
                                        onClick={() => this.applyBulkFill('sign')}>
                                        Every Sign of this Part
                                    </Button>
                                    <Button size="sm" onClick={() => this.applyBulkFill('job')}>
                                        Every Sign and Part on this Job
                                    </Button>
                                </>
                            }
                        </Col>
                        <Col xs="auto">
                            <Button size="sm" color="link"
                                onClick={() => this.setState({ bulkFill: null })}>
                                Dismiss
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </div>,
            document.body,
        )
    }

    renderPaintDropdown(sign, partIndex, part) {
        const currentIndex = PAINT_OPTIONS.indexOf(part.paint)
        return <SimpleDropdown
            index={currentIndex}
            values={PAINT_OPTIONS}
            onChange={(i) => {
                const value = PAINT_OPTIONS[i]
                if (value === 'Other') {
                    this.props.showTextFieldModal('Paint', (text) => {
                        this.updatePartField(sign.id, partIndex, 'paint', text)
                    })
                } else {
                    this.updatePartField(sign.id, partIndex, 'paint', value)
                }
            }} />
    }

    renderPartTextField(sign, part, partIndex, field, label) {
        return (
            <td key={field}>
                <Input
                    placeholder={label}
                    value={part[field]}
                    onChange={(e) =>
                        this.updatePartField(sign.id, partIndex, field, e.target.value)}
                />
            </td>
        )
    }

    renderPartRow(sign, part, partIndex) {
        // Column order: Part #, Description, Material, Size, Thickness,
        // Paint, Background Color, Textile Colors, Corners, Edges, Finish.
        return (
            <tr key={partIndex}>
                {this.renderPartTextField(sign, part, partIndex, 'part_number', 'Part #')}
                {this.renderPartTextField(sign, part, partIndex, 'description', 'Description')}
                {this.renderPartTextField(sign, part, partIndex, 'material', 'Material')}
                {this.renderPartTextField(sign, part, partIndex, 'size', 'Size')}
                {this.renderPartTextField(sign, part, partIndex, 'thickness', 'Thickness')}
                <td>{this.renderPaintDropdown(sign, partIndex, part)}</td>
                {this.renderPartTextField(
                    sign, part, partIndex, 'background_color', 'Background Color')}
                {this.renderPartTextField(
                    sign, part, partIndex, 'textile_colors', 'Textile Colors')}
                {this.renderPartTextField(sign, part, partIndex, 'corners', 'Corners')}
                {this.renderPartTextField(sign, part, partIndex, 'edges', 'Edges')}
                {this.renderPartTextField(sign, part, partIndex, 'finish', 'Finish')}
                <td>
                    <Button color="danger"
                        onClick={() => this.removePart(sign.id, partIndex)}>Delete</Button>
                </td>
            </tr>
        )
    }

    renderSignImageSlot(sign, category, label, displayLabel) {
        const existingFile = this.findDrawingImage(sign.given_id, category)
        return (
            <DrawingImageUploadView
                label={label}
                displayLabel={displayLabel}
                uploadFn={(file, name, onProgress) =>
                    replaceDrawingImage(
                        this.props.id, sign.given_id, category, file, name, onProgress)}
                buildName={(mimeType) =>
                    `Drawing Images/${sign.given_id}/${label}.${extensionForMimeType(mimeType)}`}
                imageUrl={existingFile ?
                    getDownloadFileLink(existingFile.id, existingFile.name) : null}
                downloadName={existingFile?.name}
                onUploaded={() => this.refreshFiles()}
                showError={this.props.showError}
                />
        )
    }

    renderSign(sign) {
        const drawingData = this.state.drawingDataBySignId[sign.id]
        const dirty = !!this.state.dirtyBySignId[sign.id]

        return (
            <div key={sign.id} style={{ marginBottom: '40px' }}
                onBlur={this.scheduleHideBulkFillBar}
                onFocus={this.cancelHideBulkFillBar}>
                <h3>{sign.given_id}</h3>
                <Row>
                    <Col>
                        <Label>Quantity</Label>
                        <Input value={this.signQuantity(sign)} disabled />
                    </Col>
                    <Col>
                        <Label>Font</Label>
                        <Input value={drawingData.font}
                            onChange={(e) => this.updateSignField(sign.id, 'font', e.target.value)} />
                    </Col>
                    <Col>
                        <Label>Mounting</Label>
                        <Input value={drawingData.mounting}
                            onChange={(e) =>
                                this.updateSignField(sign.id, 'mounting', e.target.value)} />
                    </Col>
                </Row>
                <Row style={{ marginBottom: '10px' }}>
                    <Col lg="6">
                        {this.renderSignImageSlot(sign, 'drawing_dimension', 'Dimension Drawing')}
                        <Label>Scale</Label>
                        <InputGroup>
                            <Input type="number" step="0.01"
                                value={drawingData.dimension_scale}
                                onChange={(e) => this.updateSignField(
                                    sign.id, 'dimension_scale',
                                    parseFloat(e.target.value))} />
                            <InputGroupText>%</InputGroupText>
                        </InputGroup>
                    </Col>
                </Row>
                <Row style={{ marginBottom: '10px' }}>
                    <Col lg="6">
                        {this.renderSignImageSlot(
                            sign, 'drawing_overview', 'Overview / Snapshot of All Signs')}
                        <Label>Scale</Label>
                        <InputGroup>
                            <Input type="number" step="0.01"
                                value={drawingData.overview_scale}
                                onChange={(e) => this.updateSignField(
                                    sign.id, 'overview_scale',
                                    parseFloat(e.target.value))} />
                            <InputGroupText>%</InputGroupText>
                        </InputGroup>
                    </Col>
                </Row>
                <Row><Col><div style={{ overflowX: 'auto' }}><Table>
                    <thead>
                        <tr>
                            <th>Part #</th>
                            <th>Description</th>
                            <th>Material</th>
                            <th>Size</th>
                            <th>Thickness</th>
                            <th>Paint (Surface/Subsurface)</th>
                            <th>Background Color</th>
                            <th>Textile Colors</th>
                            <th>Corners</th>
                            <th>Edges</th>
                            <th>Finish</th>
                            <th>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {drawingData.part_data.map((part, partIndex) =>
                            this.renderPartRow(sign, part, partIndex))}
                    </tbody>
                </Table></div></Col></Row>
                <Row style={{ marginTop: '10px' }}>
                    <Col>
                        <Button color="primary" style={{ marginRight: '8px' }}
                            onClick={() => this.addPart(sign.id)}>Add Part</Button>
                        <Button color="success" disabled={!dirty}
                            onClick={() => this.saveSign(sign.id)}>Save</Button>
                    </Col>
                </Row>
            </div>
        )
    }

    renderCompanyLogoSlot() {
        const { customerName } = this.state
        if (!customerName) {
            return null
        }
        return (
            <Row style={{ marginBottom: '20px' }}>
                <Col lg="6">
                    <DrawingImageUploadView
                        label={`${customerName} Logo`}
                        uploadFn={(file, name, onProgress) =>
                            uploadCompanyLogo(file, name, onProgress, customerName)}
                        buildName={(mimeType) =>
                            `${customerName} Logo.${extensionForMimeType(mimeType)}`}
                        imageUrl={getCompanyLogoLink(customerName)}
                        mayNotExist
                        downloadName={`${customerName} Logo`}
                        onUploaded={() => {}}
                        showError={this.props.showError}
                        />
                </Col>
            </Row>
        )
    }

    generateProof() {
        this.props.turnOnLoader()
        generateDrawingDataPdf(this.props.id).then(
            (result) => {
                window.open(
                    getDownloadFileLink(result.data.id, 'Drawing Data.pdf'),
                    '_blank')
                this.refreshFiles()
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            },
        )
    }

    render() {
        const anyDirty = Object.values(this.state.dirtyBySignId).some(Boolean)
        return (
            <Container>
                {this.renderBulkFillBar()}
                {this.props.breadCrumbs(6, this.props.id)}
                <h1 style={{ marginTop: '20px', marginBottom: '20px' }}>Drawing Data</h1>
                {this.renderCompanyLogoSlot()}
                {this.state.signs.length === 0 ?
                    <p>No signs on this quote yet.</p> :
                    this.state.signs.map((sign) => this.renderSign(sign))}
                {this.state.signs.length > 0 &&
                    <Row className="quote-bottom-bottons-row" xs="1" sm="2">
                        <Col>
                            <Button color="success" size="lg" disabled={!anyDirty}
                                className="default-weight purchase-button"
                                onClick={() => this.saveAll()}>Save All</Button>
                        </Col>
                        <Col>
                            <Button color="primary" size="lg"
                                className="default-weight purchase-button"
                                style={{ marginRight: '8px' }}
                                onClick={() => this.generateProof()}>Generate Proof</Button>
                        </Col>
                    </Row>}
            </Container>
        )
    }
}

export default DrawingDataViewController
